/**
 * 견적(Quote) API (P9-UI Step 3 — 견적 섹션).
 *
 * quote + quote_item 생성/수정/발송/수락/거절, 견적서 다운로드,
 * 견적 소스(딜 RFQ) 후보 + 최근 견적 단가, 모달 prefill.
 */

import type { QuoteId } from '../lib/brand';
import type { CustomerPaper } from '../pages/quote/paper-0-cells';   // 직렬화 계약 타입(paper-0) — API 경계를 건너는 모양이라 여기서 쓴다
import { z } from 'zod';
import {
  API_BASE, authHeaders, handleUnauthorized,
  getJson, postJson, patchJson, loadingFetch, ApiConflictError, parseResponse, saveBlobAsFile,
  httpError, errorDetail,
} from './http';
import {
  QuoteResponseSchema, QuoteFullResponseSchema, DealQuotesResponseSchema,
  type Quote, type QuoteItem, type QuoteFull, type QuoteStatus, type QuoteResult,
} from './schemas';

// ─── Quote API (P9-UI Step 3 — 견적 섹션) ────────────────────────────
// 응답 타입은 shared z.infer가 정본(schemas.ts 관문) — 손 정의 interface 제거(2026-07-12).
// 모든 응답은 zod 검증을 거침 — 서버·클라 타입 드리프트 즉시 발견.
export type { Quote, QuoteItem, QuoteFull, QuoteStatus, QuoteResult };

export interface DealQuotesResponse {
  quotes: Quote[];
}

/**
 * GET /api/admin/deal/:id/quotes — 한 deal 의 quote 목록.
 * 정렬: created_at DESC. v1 페이징 없음 (최대 100).
 */
export async function fetchDealQuotes(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<DealQuotesResponse> {
  return getJson<DealQuotesResponse>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/quotes`,
    { ...opts, schema: DealQuotesResponseSchema },
  );
}

// ─── Quote 생성 + 발송 (P9-UI Step 3b) ──────────────────────────────
/**
 * QuoteItem 입력 (생성 시).
 *
 * 가격:
 *   - unit_price_krw 가 있으면 그대로 사용.
 *   - 없고 unit_price_usd 만 있으면 백엔드에서 quote.fx_rate × usd 환산
 *     (현재 backend 는 자동 환산 X — UI 가 합산하면서 보내는 패턴).
 *
 * 무결성: option_part 또는 description 중 하나 필수.
 */
export interface QuoteItemInput {
  deal_item_id?:    number | null;
  sort_order?:      number;
  category?:        string | null;
  option_part?:     string | null;
  description?:     string | null;
  quantity?:        number | null;
  unit_price_usd?:  number | null;
  unit_price_krw?:  number | null;
  cost_basis_usd?:      number | null;
  source_type?:         'stock' | 'deal_rfq' | 'rfq_history' | null;
  source_ref_id?:       number | null;
  cost_basis_amount?:   number | null;
  cost_basis_currency?: string | null;
  notes?:           string | null;
  // ─── 서버 그룹 (마이그 131) ───────────────────────────────────────
  /** 'server' = 서버 헤더행. 미전송이면 백엔드가 'part' 로 채운다. */
  line_type?:           'server' | 'part';
  /** 서버 구성품이면 부모 서버행의 **deal_item.id**(quote_item.id 아님). */
  parent_deal_item_id?: number | null;
  /** 서버 대수. 헤더행 필수(DB CHECK). */
  server_quantity?:     number | null;
  /** 서버 1대 판매가(번들가). 고객 총액 = 이 값 × server_quantity. */
  unit_price_override?: number | null;
  // ─── 임대 (마이그 178/179/185) ────────────────────────────────────
  //   전부 선택 — 판매 견적은 키를 싣지 않는다. NULL 이면 임대 라인이 아니다.
  rental_term_months?:          number | null;
  rental_preset_id?:            number | null;
  rental_margin_rate?:          number | null;
  rental_manual_unit_krw?:      number | null;
  rental_contract_cost_krw?:    number | null;
  rental_short_term_months?:    number | null;
  rental_short_term_surcharge?: number | null;
  rental_normal_term_months?:   number | null;
  rental_short_term_step_up?:   number | null;
  rental_penalty_rate?:         number | null;
  rental_penalty_adjust?:       number | null;
  /**
   * 원가 항목 스냅샷.
   *
   * ★ **키를 싣지 않으면 서버가 기존 스냅샷을 그대로 둔다**(saveRentalCosts).
   *   임대 견적을 판매로 되돌릴 때는 빈 배열을 명시해야 지워진다.
   */
  rental_costs?: {
    label: string; kind: string; value: number;
    amount_krw: number; sort_order: number;
  }[];
}

// QuoteItem (DB row) — shared QuoteItemSchema의 z.infer가 정본 (위 re-export 참조).

/** 견적 소스(딜 RFQ) 후보 — GET /api/admin/rfq/deal-sources?deal_id=. */
export interface DealRfqSource {
  rfq_id:            number;
  need_id:           number;
  option_part:       string | null;
  alternative_part:  string | null;   // 대체품 파트넘버
  is_alternative:    boolean;         // 이 파트 레퍼런스에 "대체"로 뜨는 행
  supplier_id:       number;
  supplier_name:     string;
  price_amount:      number | null;
  price_currency:    string | null;
  sourced_condition: string | null;
  requested_condition: string | null; // 니즈 요청 컨디션 — sourced 없을 때 폴백
  status:            string;
  rfq_date:          string;          // ISO — 소스 라벨용
}
/** 최근 견적 단가 — 발송된 견적의 파트별 판매단가(KRW) 최대 3. deal-sources 응답에 동봉. */
export interface DealQuotePrice {
  option_part:    string;
  // deal_id·event_type 는 신규 필드 — 구버전 백엔드(미전송) 대비 optional.
  //   (신필드를 required 로 두면 백엔드가 안 보낼 때 파싱이 깨져 견적서가 통째로
  //    사라진다 — 2026-08 실제 아웃티지 재발 방지.)
  deal_id?:       string;          // 참조 딜 id — 거래처명 클릭 시 /deal/:id 새탭
  quoted_at:      string;          // ISO — 이벤트(견적발송/수주) 발생 시각
  customer_name:  string | null;   // 거래처
  unit_price_krw: number | null;
  event_type?:    '견적' | '수주';  // 견적발송 / 수주 (미전송이면 렌더에서 '견적' 취급)
}
const DealRfqSourcesResponseSchema = z.object({
  sources: z.array(z.object({
    rfq_id:              z.number(),
    need_id:             z.number(),
    option_part:         z.string().nullable(),
    alternative_part:    z.string().nullable(),
    is_alternative:      z.boolean(),
    supplier_id:         z.number(),
    supplier_name:       z.string(),
    price_amount:        z.number().nullable(),
    price_currency:      z.string().nullable(),
    sourced_condition:   z.string().nullable(),
    requested_condition: z.string().nullable(),
    status:              z.string(),
    rfq_date:            z.string(),
  })),
  quotePrices: z.array(z.object({
    option_part:    z.string(),
    deal_id:        z.string().optional(),
    quoted_at:      z.string(),
    customer_name:  z.string().nullable(),
    unit_price_krw: z.number().nullable(),
    event_type:     z.enum(['견적', '수주']).optional(),
  })).optional(),
});

export async function fetchDealRfqSources(
  dealId: string | number,
): Promise<{ sources: DealRfqSource[]; quotePrices: DealQuotePrice[] }> {
  const r = await getJson<{ sources: DealRfqSource[]; quotePrices?: DealQuotePrice[] }>(
    `/api/admin/rfq/deal-sources?deal_id=${encodeURIComponent(String(dealId))}`,
    { schema: DealRfqSourcesResponseSchema },
  );
  return { sources: r.sources, quotePrices: r.quotePrices ?? [] };
}

/**
 * POST /api/admin/quote/full body — Quote + items 한 번에.
 *
 * UI 가 합산값 (total_amount_*) 까지 채워서 보냄 — backend 는 받은 값 그대로 저장.
 * (자동 합산은 향후 service 에 추가 예정)
 */
export interface QuoteFullCreateInput {
  deal_id:                  number;
  name:                     string;
  contact_id?:              number | null;
  currency?:                'KRW' | 'USD';
  /** 견적 유형 (마이그 178). 미전송이면 백엔드가 'SALE' 로 채운다. */
  quote_type?:              'SALE' | 'RENTAL';
  total_amount_usd?:        number | null;
  total_amount_krw?:        number | null;
  amount_with_vat_krw?:     number | null;
  cost_basis_amount_krw?:   number | null;
  margin_krw?:              number | null;
  margin_rate?:             number | null;
  fx_rate_krw_per_usd?:     number | null;
  valid_until?:             string | null;        // 'YYYY-MM-DD'
  payment_terms?:           string | null;
  proposal_content?:        string | null;
  evidence_data?:           string | null;
  notes?:                   string | null;
  items:                    QuoteItemInput[];
}

// QuoteFull(quote+items) — shared 파생 z.infer가 정본 (위 re-export 참조).

/**
 * POST /api/admin/quote/full — quote + items 트랜잭션 생성.
 * 응답: 생성된 QuoteFull (id, code 등 채워짐).
 */
export async function createQuoteFull(
  body: QuoteFullCreateInput,
): Promise<QuoteFull> {
  return postJson<QuoteFull>('/api/admin/quote/full', body, QuoteFullResponseSchema);
}

/** 견적 수정 — PUT /:id/full. items 는 id 기준 diff(있으면 수정, 없으면 신규, 빠지면 삭제). */
export interface QuoteItemSyncInput {
  id?:             string | null;   // 기존 quote_item id (없으면 신규)
  deal_item_id?:   number | null;
  sort_order?:     number;
  category?:       string | null;
  option_part?:    string | null;
  description?:    string | null;
  quantity?:       number | null;
  unit_price_usd?: number | null;
  unit_price_krw?: number | null;
  cost_basis_usd?: number | null;
  source_type?:         'stock' | 'deal_rfq' | 'rfq_history' | null;
  source_ref_id?:       number | null;
  cost_basis_amount?:   number | null;
  cost_basis_currency?: string | null;
  notes?:          string | null;
  // 서버 그룹 (마이그 131) — 생성 payload 와 같은 계약.
  line_type?:           'server' | 'part';
  parent_deal_item_id?: number | null;
  server_quantity?:     number | null;
  unit_price_override?: number | null;
}
export interface QuoteFullUpdateInput {
  name?:                  string;
  currency?:              'KRW' | 'USD';
  /** 견적 유형 (마이그 178). 저장 시 유형 전환도 이 값으로 간다. */
  quote_type?:            'SALE' | 'RENTAL';
  total_amount_usd?:      number | null;
  total_amount_krw?:      number | null;
  amount_with_vat_krw?:   number | null;
  cost_basis_amount_krw?: number | null;
  margin_krw?:            number | null;
  margin_rate?:           number | null;
  fx_rate_krw_per_usd?:   number | null;
  valid_until?:           string | null;
  payment_terms?:         string | null;
  proposal_content?:      string | null;
  evidence_data?:         string | null;
  notes?:                 string | null;
  items:                  QuoteItemSyncInput[];
  /** 낙관적 잠금 (마이그 114) — 모달 연 시점의 deal.version. 불일치 시 409. */
  expected_version?:      number;
}
export async function updateQuoteFull(id: string | number, body: QuoteFullUpdateInput): Promise<QuoteFull> {
  const res = await loadingFetch(`${API_BASE}/api/admin/quote/${encodeURIComponent(String(id))}/full`, {
    method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBodyDetail = errorDetail(await res.json().catch(() => null));
    if (res.status === 409) {
      throw new ApiConflictError(errBodyDetail || '딜이 다른 곳에서 수정되었습니다.');
    }
    throw new Error(`견적 수정 실패 (HTTP ${res.status})`);
  }
  return parseResponse(await res.json(), QuoteFullResponseSchema, 'PUT /api/admin/quote/:id/full');
}

/**
 * POST /api/admin/quote/:id/send — draft → sent (멱등).
 * 이미 sent 이상이면 그대로 반환 (에러 X).
 */
export async function sendQuote(id: string | number): Promise<Quote> {
  return postJson<Quote>(
    `/api/admin/quote/${encodeURIComponent(String(id))}/send`,
    {},
    QuoteResponseSchema,
  );
}

/**
 * POST /api/admin/quote/:id/accept — 수락 (P9-UI Step 3e).
 * 같은 deal 의 기존 accepted → sent 로 되돌림.
 * 이 quote → accepted + result='수락'.
 * deal cache (final_*_krw) + status='수주' 자동 변경.
 * 채팅 알림 자동.
 */
export async function acceptQuote(id: string | number): Promise<Quote> {
  return postJson<Quote>(
    `/api/admin/quote/${encodeURIComponent(String(id))}/accept`,
    {},
    QuoteResponseSchema,
  );
}

/**
 * POST /api/admin/quote/:id/reject — 거절 (P9-UI Step 3e).
 * status='rejected' + result='거절'.
 * 이전이 accepted 였으면 deal 캐시 NULL.
 * deal.status 변경 X. 채팅 알림 자동.
 */
export async function rejectQuote(id: string | number): Promise<Quote> {
  return postJson<Quote>(
    `/api/admin/quote/${encodeURIComponent(String(id))}/reject`,
    {},
    QuoteResponseSchema,
  );
}

/**
 * DELETE /api/admin/quote/:id — 견적 삭제 (soft delete).
 * 작성됨(draft) 상태만 삭제 가능. 남은 작성 견적이 없으면 딜 상태가
 * '견적 작성 완료' → '리드 검토됨' 으로 되돌아간다(서버 처리).
 */
export async function deleteQuote(id: string | number): Promise<void> {
  const res = await loadingFetch(
    `${API_BASE}/api/admin/quote/${encodeURIComponent(String(id))}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
}

/**
 * GET /api/admin/quote/:id/full — Quote + items (P9-UI Step 3c 보기 모달용).
 */
export async function fetchQuoteFull(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<QuoteFull> {
  return getJson<QuoteFull>(
    `/api/admin/quote/${encodeURIComponent(String(id))}/full`,
    { ...opts, schema: QuoteFullResponseSchema },
  );
}

/**
 * GET /api/admin/quote/:id/document — 견적서 xlsx 다운로드.
 * 바이너리 응답을 받아 브라우저 다운로드를 트리거한다.
 */
export type QuoteDocPreset =
  | 'part' | 'server-full' | 'server-brief'      // 구매
  | 'part-rental' | 'server-rental';             // 임대 — 사람이 고르지 않는다(백엔드가 정함)

/** 이 견적에 서버 품목이 있는가 — 다운로드 팝업을 띄울지 판정 (§13-1). */
export async function quoteDocumentHasServer(id: string | number): Promise<boolean> {
  const res = await loadingFetch(
    `${API_BASE}/api/admin/quote/${encodeURIComponent(String(id))}/document-kinds`,
    { headers: authHeaders() },
  );
  if (res.status === 401) { handleUnauthorized(); throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.'); }
  if (!res.ok) throw new Error(`견적 정보 조회 실패 (HTTP ${res.status})`);
  const body = await res.json() as { has_server?: unknown };
  return body.has_server === true;
}

export async function downloadQuoteDocument(
  id: string | number, filenameOverride?: string, preset?: QuoteDocPreset,
): Promise<void> {
  const qs = preset ? `?preset=${preset}` : '';
  const res = await loadingFetch(`${API_BASE}/api/admin/quote/${encodeURIComponent(String(id))}/document${qs}`, {
    headers: authHeaders(),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) {
    throw new Error(`견적서 생성 실패 (HTTP ${res.status})`);
  }
  // 파일명 결정: 1) 호출자 override(크로스오리진에서 헤더 못 읽는 문제 회피 — 권장)
  //   2) Content-Disposition(filename*→filename)  3) fallback quote-{id}.xlsx
  let filename = filenameOverride?.trim() || `quote-${id}.xlsx`;
  if (!filenameOverride?.trim()) {
    const cd = res.headers.get('Content-Disposition') ?? '';
    const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    if (star?.[1]) filename = decodeURIComponent(star[1]);
    else if (plain?.[1]) filename = plain[1];
  }

  saveBlobAsFile(await res.blob(), filename);
}

/**
 * 고객 지면 → xlsx 다운로드. 프론트가 채운 고객 지면(칸 주소·서식 번호·문구)을 그대로 보내면
 * 백엔드가 ExcelJS 로 같은 칸 배치를 그린다 — 웹 렌더와 같은 정본에서 나온 v3 견적서.
 * (POST /:id/document-onepage — hcltech-parts quote.routes)
 */
export async function downloadPaperXlsx(quoteId: QuoteId, paper: CustomerPaper, filename: string): Promise<void> {
  const res = await loadingFetch(`${API_BASE}/api/admin/quote/${encodeURIComponent(String(quoteId))}/document-onepage`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ tpl: paper, filename }),      // 필드명 tpl 은 백엔드 zod 계약
  });
  if (res.status === 401) { handleUnauthorized(); throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.'); }
  if (!res.ok) throw new Error(`견적서(xlsx) 생성 실패 (HTTP ${res.status})`);
  saveBlobAsFile(await res.blob(), filename);
}

// ─── 견적 모달 prefill (cost_basis_usd lookup) — P9-UI Step 3d ──────
/**
 * 각 deal_item 의 기준 원가 USD 추정값 — RFQ 응답에서 lookup.
 * lookup 실패 시 null (영업담당자 수동 입력 가능).
 */
export interface QuotePrefillItem {
  deal_item_id:   string;
  cost_basis_usd: number | null;
}
export interface QuotePrefillResponse {
  items: QuotePrefillItem[];
}

const QuotePrefillResponseSchema = z.object({
  items: z.array(z.object({
    deal_item_id:   z.string(),
    cost_basis_usd: z.number().nullable(),
  })),
});

export async function fetchDealQuotePrefill(
  id: string | number,
  opts: { signal?: AbortSignal } = {},
): Promise<QuotePrefillResponse> {
  return getJson<QuotePrefillResponse>(
    `/api/admin/deal/${encodeURIComponent(String(id))}/quote-prefill`,
    { ...opts, schema: QuotePrefillResponseSchema },
  );
}

// ─── 견적 편집 데이터 (GET /quote/:id/editor) — 지면 페이지 1 fetch ─────
export interface QuoteEditorApiRow {
  id: string; category: string | null; option_part: string | null;
  requested_part: string | null; requested_condition: string | null;
  is_base_part: boolean; is_unregistered: boolean; parent_quote_item_id: string | null;
  deal_item_id: string | null; parent_deal_item_id: string | null;   // 딜 구성 링크 — 딜에서 만든 견적의 서버-부품 묶음 열쇠
  quantity: number | null; unit_price_usd: string | null; unit_price_krw: string | null;
  server_quantity: number | null; unit_price_override: string | null; description: string | null;
  cost_basis_usd: string | null; cost_basis_amount: string | null; cost_basis_currency: string | null;
  source_type: string | null; source_ref_id: string | null;
  source_name: string | null;        // 부착 소스 이름(재고 창고명 / RFQ 공급사명)
  source_condition: string | null;   // 재고 소스의 컨디션 — RFQ 는 null (백엔드 next: quote.queries findQuoteEditorItems)
  rental_term_months: number | null;       // 178 임대 기간(개월) — 임대 견적서 지면
  rental_contract_cost_krw: string | null;  // 178 임대총원가(라인) BIGINT→text — 임대 마진
  line_type: string | null; sort_order: number;
}
export interface QuoteEditorApiHeader {
  quote_no: string | null; issued_at: string | null; valid_until: string | null;
  deal_name: string | null; customer_name: string | null;
  contact_name: string | null; contact_phone: string | null; contact_email: string | null;
  assignee_name: string | null; assignee_email: string | null;
}
export interface QuoteEditorApiData {
  quote: { id: string; quote_no: string; quote_type: string; valid_until: string | null };
  doc_type: 'server' | 'part'; is_rental: boolean; fx: number; deal_id: string;
  header: QuoteEditorApiHeader | null; rows: QuoteEditorApiRow[];
}
export async function fetchQuoteEditorData(
  quoteId: QuoteId, opts: { signal?: AbortSignal } = {},
): Promise<QuoteEditorApiData> {
  return getJson<QuoteEditorApiData>(
    `/api/admin/quote/${encodeURIComponent(String(quoteId))}/editor`, { ...opts },
  );
}

/** 견적 항목 필드 단위 저장(소스 부착·인라인). PATCH /quote/:id/item/:itemId → 최신 편집 데이터. */
export interface QuoteItemPatchBody {
  quantity?: number | null; unit_price_krw?: number | null; unit_price_usd?: number | null;
  requested_condition?: string | null; category?: string | null;
  option_part?: string | null; requested_part?: string | null; description?: string | null;
  source_type?: 'stock' | 'deal_rfq' | 'rfq_history' | null; source_ref_id?: string | null;
  cost_basis_amount?: number | null; cost_basis_currency?: string | null;
}
/** 새 항목 하나 — 원페이지 품목 추가. parent_quote_item_id 로 서버 지정(독립·파트 견적이면 null). */
export interface NewQuoteItemBody {
  category: string | null;
  option_part: string | null;
  description: string | null;
  quantity: number;
  parent_quote_item_id: string | null;
}
/** 품목 추가 — POST /quote/:id/items (여러 개 한 번에). 응답은 편집 데이터 전체(재채움 재료). */
export async function addQuoteEditorItems(quoteId: QuoteId, items: NewQuoteItemBody[]): Promise<QuoteEditorApiData> {
  return postJson<QuoteEditorApiData>(`/api/admin/quote/${encodeURIComponent(String(quoteId))}/items`, { items });
}

export async function patchQuoteEditorItem(
  quoteId: QuoteId, itemId: string | number, body: QuoteItemPatchBody,
): Promise<QuoteEditorApiData> {
  return patchJson<QuoteEditorApiData>(
    `/api/admin/quote/${encodeURIComponent(String(quoteId))}/item/${encodeURIComponent(String(itemId))}`,
    body,
  );
}
